// artifacts/text/client.tsx
"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactMarkdown from "react-markdown";
import { Artifact } from "@/components/create-artifact";
import { DocumentSkeleton } from "@/components/document-skeleton";
import {
  ClockRewind,
  CopyIcon,
  MessageIcon,
  PenIcon,
  RedoIcon,
  UndoIcon,
} from "@/components/icons";
import { toast } from "sonner";
import { Markdown } from "@/components/markdown";
import { Editor } from "@/components/text-editor";
import type { InitializeParameters } from "@/types";
import { LoaderIcon } from "@/components/icons";
import { usePathname, useParams } from "next/navigation";

interface TextArtifactMetadata {
  suggestions: any[];
}

export const textArtifact = new Artifact<"text", TextArtifactMetadata>({
  kind: "text",
  description:
    "Useful for text content, like drafting essays, reports, or business plans.",

  // ✅ Must accept `params` argument
  initialize: async (params: InitializeParameters<TextArtifactMetadata>) => {
    // You can set initial metadata if needed
    params.setMetadata?.({
      suggestions: [],
    });
  },

  onStreamPart: ({ streamPart, setMetadata, setArtifact }) => {
    // if (streamPart.type === "data-suggestion") {
    //   setMetadata((metadata) => ({
    //     suggestions: [...metadata.suggestions, streamPart.data],
    //   }));
    // }

    if (streamPart.type === "data-textDelta") {
      setArtifact((draftArtifact) => ({
        ...draftArtifact,
        content: draftArtifact.content + streamPart.data,
        isVisible: true,
        status: "streaming",
      }));
    }
  },

  // ✅ Required empty placeholders if `actions` and `toolbar` are mandatory
  actions: [],
  toolbar: [],

  content: ({
    content,
    status,
    isLoading,
    metadata,
    onSaveContent,
    currentVersionIndex,
    isCurrentVersion,
    setArtifact,
  }) => {
    const pathname = usePathname();
    const { parent_scr_id } = useParams();
    const htmlContentNew = useSelector(
      (state: RootState) => state.html.htmlContent
    );
    const [isEditing, setIsEditing] = useState(false);
    const [isStreamDone, setIsStreamDone] = useState(false);
    const [localContent, setLocalContent] = useState(content || "");
    const [isSaving, setIsSaving] = useState(false);
    const {
      isTransparentLoading,
      userData,
      saveDbStore,
      forStreamingHtml,
      activeTab,
    } = useSelector((state) => state.main);
    const effectiveTab = activeTab ?? "Chat";

    useEffect(() => {
      if (status !== "streaming" && content) {
        setIsStreamDone(true);
      }
    }, [status, content]);


    let chatType = "chat";
    if (effectiveTab === "Go-to-Market Strategy") {
      chatType = "go_to_market_strategy";
    } else if (effectiveTab !== "Chat") {
      chatType = effectiveTab.toLowerCase().replace(/[\s\/]+/g, "_");
    }

    const handleSaveToServer = useCallback(
      async (payloadContent?: string) => {
        const toSave = payloadContent ?? localContent;
        if (!toSave?.trim()) return;

        setIsSaving(true);
        try {
          const user_id = userData._id;
          const screen_id = saveDbStore.screen_id;
          console.log(user_id, "--user_id--screen_id--", screen_id);
          let apiUrl;
          let payload;
          let isProject = pathname.startsWith("/project");
console.log(saveDbStore, "---saveDbStoresaveDbStore")
          // 🔥 If the route contains `/project`, call project logs API
          if (isProject) {
            apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project`;
            payload = {
              user_id: userData._id,
              parent_scr_id: parent_scr_id,
              screen_id: saveDbStore.screen_id,
              prompt: "Document Editor",
              chat_type: chatType,
              content: toSave,
              is_followup: saveDbStore.is_followup,
              creditUsed: saveDbStore.creditUsed,
            };
          }
          // 🔥 Otherwise call website logs API
          else {
            apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website`;
            payload = {
              user_id: userData._id,
              screen_id: saveDbStore.screen_id,
              prompt: "Document Editor",
              chat_type: chatType,
              content: toSave,
              html_file_detail: "",
              is_followup: saveDbStore.is_followup,
              creditUsed: saveDbStore.creditUsed,
            };
          }

          // const res = await fetch(
          //   `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website`,
          //   {
          //     method: "POST",
          //     headers: { "Content-Type": "application/json" },
          //     body: JSON.stringify({
          //       user_id: userData._id,
          //       screen_id: saveDbStore.screen_id,
          //       prompt: "Document Editor",
          //       chat_type: chatType,
          //       content: toSave,
          //       html_file_detail: "",
          //       is_followup: saveDbStore.is_followup,
          //       creditUsed: saveDbStore.creditUsed,
          //     }),
          //   }
          // );
          const res = await fetch(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (data.status === 1) {
            onSaveContent?.(toSave);
          } else {
            console.error("Save failed:", data.message);
          }
        } catch (err) {
          console.error("Error saving document:", err);
        } finally {
          setIsSaving(false);
        }
      },
      [localContent, onSaveContent]
    );

    useEffect(() => {
      setLocalContent(content || "");
    }, [content]);

    useEffect(() => {
      if (!isStreamDone) return;
      const handleClick = () => setIsEditing(true);
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }, [isStreamDone]);

    if (isLoading) {
      return <DocumentSkeleton artifactKind="text" />;
    }
    const waitingText = "Waiting for document";

    const noHtmlAvailable =
      !htmlContentNew &&
      !forStreamingHtml &&
      !content &&
      status !== "streaming";
    if (noHtmlAvailable) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="flex flex-col flex-1 items-center justify-center">
            <div className="animate-spin h-[50px] w-[50px]">
              <LoaderIcon size={50} />
            </div>
            <div className="p-4">{waitingText}</div>
          </div>
        </div>
      );
    }
    return (
      <div className="relative flex flex-col py-8 md:p-20 px-4">
        {isTransparentLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
        {/* 
        {!isEditing ? (
          <div className="prose max-w-none dark:prose-invert">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <Editor
            content={localContent}
            // onSaveContent={() => handleSaveToServer(localContent)}
            onSaveContent={(updatedContent) => {
              setLocalContent(updatedContent); // update local state
              handleSaveToServer(updatedContent); // save actual new content
            }}
            suggestions={metadata?.suggestions || []}
            status={status}
          />
        )} */}

        {chatType === "chat" ? (
          <div className="prose max-w-none dark:prose-invert">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <>
            {!isEditing ? (
              <div className="prose max-w-none dark:prose-invert">
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>
            ) : (
              <Editor
                content={localContent}
                onSaveContent={(updatedContent) => {
                  setLocalContent(updatedContent);
                  handleSaveToServer(updatedContent);
                }}
                suggestions={metadata?.suggestions || []}
                status={status}
              />
            )}
          </>
        )}

        {metadata?.suggestions?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold mb-2">Suggestions:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              {metadata.suggestions.map((s, i) => (
                <li key={i}>{s.text || s}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  },
});
